import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-grid-system";
import MultiSelect from "../../atoms/inputs/MultiSelect";
import TextField from "../../atoms/inputs/TextField";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { theme } from "../../../theme";
import Card from "../../atoms/Card";
import TableHeader from "../../atoms/TableHeader";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import TableRow from "../../atoms/TableRow";
import BodySmall from "../../atoms/typography/BodySmall";
import IconButton from "../../atoms/buttons/IconButton";
import Tooltip from "../../molecules/Tooltip";
import { EditIcon } from "../../atoms/icons/EditIcon";
import DeleteModal from "../../molecules/modals/DeleteModal";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Modal from "../../molecules/Modal";
import Select from "../../atoms/inputs/Select";
import { AddIcon } from "../../atoms/icons/AddIcon";
import { OpenLinkIcon } from "../../atoms/icons/OpenLinkIcon";
import {
  CheckedStatusCircle,
  FailedStatusCircle,
} from "../../atoms/StatusCircles";
import Checkbox from "../../atoms/inputs/Checkbox";

interface Props {
  clientIdentifier: string;
}

const securityTypeOptions: { value: string; label: string }[] = [
  {
    value: "residentalProperty",
    label: "clientFiles.securityTypes.residentalProperty",
  },
  {
    value: "business",
    label: "clientFiles.securityTypes.business",
  },
  {
    value: "other",
    label: "clientFiles.securityTypes.other",
  },
];

const ClientSecurities: React.FC<Props> = () => {
  const { t } = useTranslation();
  const [selectedSecurityTypes, setSelectedSecurityTypes] = useState<string[]>([
    "residentalProperty",
    "business",
    "other",
  ]);
  const [searchSecurity, setSearchSecurity] = useState("");
  const [addSecurityOpen, setAddSecurityOpen] = useState(false);

  return (
    <div>
      <Row nogutter align="end" style={{ gap: 24 }}>
        <MultiSelect
          options={securityTypeOptions}
          selectedOptions={selectedSecurityTypes}
          onChange={setSelectedSecurityTypes}
          title={t("clientFiles.securityType")}
        />
        <TextField
          placeholder={t("clientFiles.searchSecurity")}
          value={searchSecurity}
          onChange={setSearchSecurity}
        />
        <OutlinedButton
          title={t("clientFiles.newSecurity")}
          icon={<AddIcon color={theme.colors.primary} />}
          color={theme.colors.primary}
          onClick={() => setAddSecurityOpen(true)}
        />
      </Row>
      <HorizontalSpacer spacing={2} />

      <Card spacing={0} border={true}>
        <TableHeader>
          <Col lg={1.5}>
            <TableHeaderText>{t("clientFiles.security")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("clientFiles.limitImpactId")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("clientFiles.note")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("clientFiles.nominalAmount")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("clientFiles.loanValue")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("clientFiles.assessedValue")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("clientFiles.marketValue")}</TableHeaderText>
          </Col>
        </TableHeader>
        <TableRow align="center">
          <Col lg={1.5}>
            <BodySmall>Proprieborgen</BodySmall>
          </Col>
          <Col
            lg={1.5}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <CheckedStatusCircle size="small" />
            <BodySmall>950322-0000</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>Per Åslund</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>2 000 000 kr</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col
            lg={1.5}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Tooltip text={t("clientFiles.openSecurityInNewWindow")}>
              <IconButton
                color={theme.colors.primaryBg}
                icon={<OpenLinkIcon color={theme.colors.primary} />}
              />
            </Tooltip>
            <Tooltip text={t("clientFiles.editSecurity")}>
              <IconButton
                color={theme.colors.info20}
                icon={<EditIcon color={theme.colors.info} />}
              />
            </Tooltip>

            <DeleteModal
              deleteButtonType="iconButtonMedium"
              onClickDelete={() => console.log("delete")}
            />
          </Col>
        </TableRow>
        <TableRow align="center">
          <Col lg={1.5}>
            <BodySmall>Fastighet</BodySmall>
          </Col>
          <Col
            lg={1.5}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <FailedStatusCircle size="small" />
            <BodySmall>9xxx</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>
              Pers bostad, Åsnevägen 12, Fastighetsbeteckning 12AC
            </BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col
            lg={1.5}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Tooltip text={t("clientFiles.openSecurityInNewWindow")}>
              <IconButton
                color={theme.colors.primaryBg}
                icon={<OpenLinkIcon color={theme.colors.primary} />}
              />
            </Tooltip>
            <Tooltip text={t("clientFiles.editSecurity")}>
              <IconButton
                color={theme.colors.info20}
                icon={<EditIcon color={theme.colors.info} />}
              />
            </Tooltip>

            <DeleteModal
              deleteButtonType="iconButtonMedium"
              onClickDelete={() => console.log("delete")}
            />
          </Col>
        </TableRow>
        <TableRow align="center">
          <Col lg={1.5}>
            <BodySmall>Proprieborgen</BodySmall>
          </Col>
          <Col
            lg={1.5}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <FailedStatusCircle size="small" />
            <BodySmall>Låneborgen</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>Sami Niku</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>-</BodySmall>
          </Col>
          <Col
            lg={1.5}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Tooltip text={t("clientFiles.openSecurityInNewWindow")}>
              <IconButton
                color={theme.colors.primaryBg}
                icon={<OpenLinkIcon color={theme.colors.primary} />}
              />
            </Tooltip>
            <Tooltip text={t("clientFiles.editSecurity")}>
              <IconButton
                color={theme.colors.info20}
                icon={<EditIcon color={theme.colors.info} />}
              />
            </Tooltip>

            <DeleteModal
              deleteButtonType="iconButtonMedium"
              onClickDelete={() => console.log("delete")}
            />
          </Col>
        </TableRow>
      </Card>

      {addSecurityOpen && (
        <Modal
          onClose={() => setAddSecurityOpen(false)}
          closeIcon
          width={800}
          title={t("clientFiles.addSecurity")}
          actionButtons={{
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setAddSecurityOpen(false),
            },
            actionButton: {
              buttonTitle: t("common.add"),
              onClick: () => console.log("add"),
            },
          }}
        >
          <HorizontalSpacer spacing={2} />
          <Row align="end">
            <Col lg={4}>
              <TextField
                label={t("clientFiles.securityName")}
                placeholder={t("clientFiles.securityNamePlaceholder")}
                fullWidth
              />
            </Col>
            <Col lg={4}>
              <Select
                label={t("clientFiles.securityType")}
                options={[
                  {
                    label: "clientFiles.securityTypes.property",
                    value: "property",
                  },
                  { label: "clientFiles.securityTypes.other", value: "other" },
                ]}
                value=""
                onChange={(value) => console.log(value)}
              />
            </Col>
            <Col lg={4}>
              <Checkbox title={t("clientFiles.affectsLimit")} checked />
            </Col>
          </Row>
          <HorizontalSpacer spacing={2} />
          <Row>
            <Col lg={4}>
              <TextField
                label={t("clientFiles.id")}
                placeholder={t("clientFiles.idPlaceholder")}
                fullWidth
              />
            </Col>
            <Col lg={4}>
              <TextField
                label={t("clientFiles.note")}
                placeholder={t("clientFiles.notePlaceholder")}
                fullWidth
              />
            </Col>
            <Col lg={4}>
              <TextField
                label={t("clientFiles.nominalAmount")}
                placeholder={t("clientFiles.nominalAmountPlaceholder")}
                fullWidth
              />
            </Col>
          </Row>
          <HorizontalSpacer spacing={2} />
          <Row>
            <Col lg={4}>
              <TextField
                label={t("clientFiles.loanValue")}
                placeholder={t("clientFiles.loanValuePlaceholder")}
                fullWidth
              />
            </Col>
            <Col lg={4}>
              <TextField
                label={t("clientFiles.assessedValue")}
                placeholder={t("clientFiles.assessedValuePlaceholder")}
                fullWidth
              />
            </Col>
            <Col lg={4}>
              <TextField
                label={t("clientFiles.marketValue")}
                placeholder={t("clientFiles.marketValuePlaceholder")}
                fullWidth
              />
            </Col>
          </Row>
          <HorizontalSpacer spacing={2} />
          <Row>
            <Col lg={4}>
              <TextField
                label={t("clientFiles.propertyDesignation")}
                placeholder={t("clientFiles.propertyDesignationPlaceholder")}
                optional
                fullWidth
              />
            </Col>
            <Col lg={4}>
              <TextField
                label={t("clientFiles.propertyPledge")}
                placeholder={t("clientFiles.propertyPledgePlaceholder")}
                optional
                fullWidth
              />
            </Col>
          </Row>
        </Modal>
      )}
    </div>
  );
};

export default ClientSecurities;
